import { supabase } from '../supabaseClient';

// Published union wage sheets (public.published_wage_rates). Rates are copied as printed from
// each local's own sheet; anyone can read rows with is_published = true. confidence and notes
// are internal research columns and are deliberately never selected for the public page.
const COLUMNS = [
  'local_name', 'trade', 'agreement', 'level', 'sort_order', 'hourly_wage', 'pct_of_journeyman',
  'health_welfare', 'pension', 'annuity', 'vacation', 'other_fringes', 'total_package',
  'total_payroll_package', 'dues_note', 'effective_start', 'effective_end', 'county_note',
  'source_url', 'retrieved_date', 'is_estimate',
].join(', ');

const PAGE = 1000; // PostgREST's default max rows per request

// Returns { rows, error }. An error is surfaced to the page instead of reading as "no data".
export async function fetchPublishedWageRates() {
  const rows = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from('published_wage_rates')
      .select(COLUMNS)
      .eq('is_published', true)
      .order('local_name', { ascending: true })
      .order('agreement', { ascending: true })
      .order('sort_order', { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) return { rows: [], error: error.message || 'load failed' };
    rows.push(...(data || []));
    if (!data || data.length < PAGE) return { rows, error: null };
  }
}

// One local's agreements, each with its rows in sort_order. Ordered by trade, then agreement.
export function agreementsForLocal(rows, localName) {
  const map = new Map();
  for (const r of rows) {
    if (r.local_name !== localName) continue;
    if (!map.has(r.agreement)) map.set(r.agreement, { agreement: r.agreement, trade: r.trade, rows: [] });
    map.get(r.agreement).rows.push(r);
  }
  const list = [...map.values()];
  list.forEach(a => a.rows.sort((x, y) => (x.sort_order ?? 0) - (y.sort_order ?? 0)));
  return list.sort((a, b) => a.trade.localeCompare(b.trade) || a.agreement.localeCompare(b.agreement));
}

// Which agreement a local's page opens on when the URL doesn't name one: an agreement whose name
// contains "Inside" or "Commercial" first, then one whose name starts with "Bricklayers", then the
// one with the most rows, then alphabetical.
// DEFAULT_AGREEMENT_OVERRIDES names the agreement for a local where that rule picks the wrong one.
const MAIN_NAME = /inside|commercial/i;
// A masonry local's agreement whose name starts with "Bricklayers" is its main one.
const BRICKLAYERS_NAME = /^Bricklayers\b/i;
const DEFAULT_AGREEMENT_OVERRIDES = {
  'Painters District Council 14': 'Painting Contract',
  // Precast and Pointers tie at 14 rows; open the council's main Masonry & Tile agreement, which is
  // also what its Masonry & Tile row opens (see localSummary).
  'BAC ADC 1': 'Pointers, Cleaners & Caulkers (BAC ADC 1)',
  // Five "MARBA Commercial - <counties>" sheets: all match the Commercial rule and could tie on
  // rows, so name the Cook, Lake & DuPage sheet (the one its list row opens; see MAIN_AGREEMENT).
  'Mid-America Carpenters Regional Council': 'MARBA Commercial - Cook, Lake & DuPage',
  // The residential sheet's name contains "Commercial" ("Residential / Light Commercial"), which
  // the Commercial rule would prefer; the local's main agreement is Building Trades.
  'Pipefitters Local 597 UA': 'Area Agreement - Building Trades (MCA of Chicago / LU 597)',
};
export function defaultAgreement(agreements) {
  const local = agreements[0]?.rows[0]?.local_name;
  const override = agreements.find(a => a.agreement === DEFAULT_AGREEMENT_OVERRIDES[local]);
  if (override) return override;
  return [...agreements].sort((a, b) =>
    (MAIN_NAME.test(b.agreement) - MAIN_NAME.test(a.agreement))
    || (BRICKLAYERS_NAME.test(b.agreement) - BRICKLAYERS_NAME.test(a.agreement))
    || (b.rows.length - a.rows.length)
    || a.agreement.localeCompare(b.agreement))[0] || null;
}

const APPRENTICE = /apprentice|improver|trainee|\byears?\b|\bmonths?\b/i;
const FOREMAN = /fore(man|men)|superintendent/i;
const JOURNEY = /journey/i;
// "Class 1 - <equipment>" rows (e.g. operating engineers' equipment classes). Checked before the
// foreman/apprentice words, which can appear in the equipment text ("Class 5 - Assistant Craft
// Foreman"). Only a level that starts with "Class <n>" counts.
const CLASS_LEVEL = /^class\s*\d+\b/i;
// Rates copied from the Illinois Department of Labor prevailing wage schedule rather than a
// union-published sheet; such agreements say so in their name.
export const IDOL_AGREEMENT = /Illinois Dept\. of Labor/i;

// Splits an agreement's rows into the display order: journey level, foremen, other
// classifications, apprentice steps. Sheets list apprentice steps either lowest-first or
// highest-first; a run printed highest-first is reversed so steps always read in the order an
// apprentice moves through them. Anything else keeps the sheet's own order.
// classSheet: no journey-level row and at least two "Class <n>" rows -- the classes are the
// journey-level rates, so none of them is promoted to a single base rate.
export function sectionRows(rows) {
  const apprentices = [], foremen = [], journey = [], other = [];
  for (const r of rows) {
    if (CLASS_LEVEL.test(r.level || '') && r.pct_of_journeyman == null) other.push(r);
    else if (r.pct_of_journeyman != null || APPRENTICE.test(r.level)) apprentices.push(r);
    else if (FOREMAN.test(r.level)) foremen.push(r);
    else if (JOURNEY.test(r.level)) journey.push(r);
    else other.push(r);
  }
  const classSheet = journey.length === 0 && other.filter(r => CLASS_LEVEL.test(r.level || '')).length >= 2;
  // No row is labelled journey level (e.g. "Base rate", "All Laborers Classifications ..."):
  // the sheet's first general classification is its base rate.
  if (journey.length === 0 && other.length > 0 && !classSheet) journey.push(other.shift());
  const pcts = apprentices.map(r => r.pct_of_journeyman).filter(p => p != null).map(Number);
  const descending = pcts.length > 1 && pcts[0] > pcts[pcts.length - 1] && pcts.every((p, i) => i === 0 || p <= pcts[i - 1]);
  if (descending) apprentices.reverse();
  return { journey, foremen, other, apprentices, classSheet };
}

// For a class sheet (see sectionRows): { min, max, count } of its "Class <n>" hourly wages.
// null for any other agreement.
export function classRange(rows) {
  const { other, classSheet } = sectionRows(rows);
  if (!classSheet) return null;
  const wages = other.filter(r => CLASS_LEVEL.test(r.level || '')).map(r => Number(r.hourly_wage)).filter(Number.isFinite);
  if (wages.length < 2) return null;
  return { min: Math.min(...wages), max: Math.max(...wages), count: wages.length };
}

// Prints a rate as published: never rounds away a printed third decimal (e.g. $54.575).
export function fmtRate(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  const decimals = Math.max(2, (String(v).split('.')[1] || '').length);
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export function fmtPct(v) {
  if (v == null || v === '') return null;
  return `${Number(v)}%`;
}

// Dates are stored as plain YYYY-MM-DD; format them in UTC so no timezone shifts the day.
export function fmtDate(iso, lang) {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(iso);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(lang === 'es' ? 'es-US' : 'en-US', {
    timeZone: 'UTC', year: 'numeric', month: 'long', day: 'numeric',
  });
}

export const distinct = (rows, key) => [...new Set(rows.map(r => r[key]).filter(v => v != null && v !== ''))];

// UI-only grouping of the sheets' trade names into trade families for the landing page. The
// database keeps each sheet's own trade name. A trade not listed here gets a family of its own
// (labelled with the trade name), so a newly loaded trade still shows up.
export const TRADE_FAMILIES = [
  { key: 'masonryTile', trades: [
    'Bricklayer / stone mason', 'Refractory bricklayer', 'Marble setter', 'Marble finisher',
    'Ceramic tile layer', 'Ceramic tile finisher', 'Terrazzo mechanic', 'Terrazzo finisher',
    'Pointer / cleaner / caulker', 'Pointer / cleaner / caulker - journeyperson assistant',
  ] },
  { key: 'electrician',          trades: ['Electrician'] },
  { key: 'plumber',              trades: ['Plumber'] },
  { key: 'technicalEngineer',    trades: ['Technical Engineer (surveying)'] },
  { key: 'laborer',              trades: ['Laborer'] },
  { key: 'painterDrywall',       trades: ['Painter', 'Drywall finisher (taper)'] },
  { key: 'glazier',              trades: ['Glazier'] },
  { key: 'cementMasonPlasterer', trades: ['Cement mason', 'Plasterer'] },
  { key: 'concretePrecast',      trades: ['Concrete specialist (precast)'] },
  { key: 'landscape',            trades: ['Landscape construction'] },
  { key: 'roofer',               trades: ['Roofer / waterproofer'] },
  { key: 'carpenter',            trades: ['Carpenter'] },
  { key: 'pipefitter',           trades: ['Pipefitter / steamfitter'] },
  { key: 'serviceTechResidential', trades: ['Service technician (residential / light commercial)'] },
  { key: 'operatingEngineer',    trades: ['Operating engineer'] },
  { key: 'ironworker',           trades: ['Ironworker'] },
  { key: 'sheetMetal',           trades: ['Sheet metal worker'] },
  { key: 'boilermaker',          trades: ['Boilermaker'] },
  { key: 'elevator',             trades: ['Elevator constructor'] },
  { key: 'sprinklerFitter',      trades: ['Sprinkler fitter'] },
];

const FAMILY_OF_TRADE = new Map(TRADE_FAMILIES.flatMap(f => f.trades.map(tr => [tr, f.key])));
export const familyKeyForTrade = trade => FAMILY_OF_TRADE.get(trade) || `trade:${trade}`;

// Families present in the data: { key, labelKey | label, trades: [trade names in the data],
// locals: [{ name, trades }] }. labelKey is an i18n key; a fallback family carries its trade
// name as `label` instead.
export function groupByFamily(rows) {
  const map = new Map();
  for (const r of rows) {
    const key = familyKeyForTrade(r.trade);
    if (!map.has(key)) {
      map.set(key, key.startsWith('trade:')
        ? { key, label: r.trade, trades: new Set(), locals: new Map() }
        : { key, labelKey: `unionWages.families.${key}`, trades: new Set(), locals: new Map() });
    }
    const fam = map.get(key);
    fam.trades.add(r.trade);
    if (!fam.locals.has(r.local_name)) fam.locals.set(r.local_name, new Set());
    fam.locals.get(r.local_name).add(r.trade);
  }
  return [...map.values()].map(f => ({
    ...f,
    trades: [...f.trades].sort((a, b) => a.localeCompare(b)),
    locals: [...f.locals.entries()]
      .map(([name, trades]) => ({ name, trades: [...trades].sort((a, b) => a.localeCompare(b)) }))
      .sort((a, b) => shortLocalName(a.name).localeCompare(shortLocalName(b.name))),
  }));
}

// Shorter display names for the landing grid and family lists. The local's own page always
// shows the full name as published.
const SHORT_LOCAL_NAMES = {
  "Chicago Laborers' District Council (Fox Valley funds)": 'Laborers Dist. Council: Fox Valley',
  "Chicago Laborers' District Council (Westchester funds)": 'Laborers Dist. Council: Westchester',
  'Cement Masons & Plasterers Local 502': 'Cement Masons & Plasterers 502',
  'Roofers & Waterproofers Local 11': 'Roofers Local 11',
  'Painters District Council 14': 'Painters Dist. Council 14',
  'Mid-America Carpenters Regional Council': 'Mid-America Carpenters',
  'Sheet Metal Workers Local 73': 'Sheet Metal Local 73',
  'Sheet Metal Workers Local 265': 'Sheet Metal Local 265',
};
export const shortLocalName = name => SHORT_LOCAL_NAMES[name] || name;

// Plain-language descriptor shown under each local's name, as an i18n key under
// unionWages.descriptors. A local missing here falls back to its trade names from the data.
const LOCAL_DESCRIPTORS = {
  'IBEW Local 134': 'electrical',
  'IBEW Local 701': 'electrical',
  'Plumbers Local 130 UA': 'plumbers',
  'BAC ADC 1': 'bacCouncil',
  'BAC Local 20': 'bac',
  'BAC Local 21': 'bac',
  'BAC Local 56': 'bac',
  'BAC Local 74': 'bac',
  'Cement Masons & Plasterers Local 502': 'cementMasons',
  'Painters District Council 14': 'painters',
  'Glaziers Local 27': 'glaziers',
  'Roofers & Waterproofers Local 11': 'roofers',
  "Chicago Laborers' District Council (Fox Valley funds)": 'laborers',
  "Chicago Laborers' District Council (Westchester funds)": 'laborers',
  'Mid-America Carpenters Regional Council': 'carpenters',
  'Pipefitters Local 597 UA': 'pipefitters',
  'IUOE Local 150': 'operatingEngineers',
  'Ironworkers Local 1': 'ironworkers',
  'Sheet Metal Workers Local 73': 'sheetMetal',
  'Sheet Metal Workers Local 265': 'sheetMetal',
  'Boilermakers Local 1': 'boilermakers',
  'IUEC Local 2': 'elevator',
  'Sprinkler Fitters Local 281': 'sprinklerFitters',
};
// Any other "BAC Local N ..." (e.g. "BAC Local 79 (Rockford)") is a Bricklayers & Allied
// Craftworkers local too.
const BAC_LOCAL = /^BAC Local \d+\b/;
export const localDescriptorKey = name => {
  const key = LOCAL_DESCRIPTORS[name] || (BAC_LOCAL.test(name) ? 'bac' : null);
  return key ? `unionWages.descriptors.${key}` : null;
};

// A local's main agreement within a trade family, for locals whose agreements in that family
// have different headline wages. Locals with one agreement there (or all at the same headline
// wage) need no entry; a local with several and no entry shows no single wage.
const MAIN_AGREEMENT = {
  'IBEW Local 701|electrician': 'Inside/Commercial Agreement (with DuPage Division NECA)',
  'IBEW Local 134|electrician': 'Electrical Construction (Inside Wireman) apprenticeship - IBEW 134 / ECA Chicago (EJATT)',
  'BAC Local 21|masonryTile': 'Bricklayers and Stone Masons (BAC ADC 1, Local 21 area)',
  'BAC Local 56|masonryTile': 'Will and Grundy Contractors Association - Bricklayers and Stone Masons',
  'Cement Masons & Plasterers Local 502|cementMasonPlasterer': 'Cement Masons (Local 502) - Three-Year Contract Agreement',
  'Painters District Council 14|painterDrywall': 'Painting Contract',
  // One Carpenter rate sheet per county group. Four print the same journey wage; Kankakee &
  // Iroquois is lower. Headline the Cook, Lake & DuPage sheet rather than a range, which would
  // read as "N trades" (one trade here) and stretch the Chicago-area figure to the outlying rate.
  'Mid-America Carpenters Regional Council|carpenter': 'MARBA Commercial - Cook, Lake & DuPage',
  // Building Trades and Metal Trades are both "Pipefitter / steamfitter"; Building Trades is the
  // main sheet. (The residential sheet is its own trade family.)
  'Pipefitters Local 597 UA|pipefitter': 'Area Agreement - Building Trades (MCA of Chicago / LU 597)',
};

const headlineOf = agreement => sectionRows(agreement.rows).journey[0] || null;

// { agreement, wage, range, tradeCount | classCount } for a local within a family: the agreement its
// row opens, and the headline hourly wage to show for it. When the family's agreements have different
// wages and none is the main one, wage is null and range = { min, max } of their journey-level
// headline wages, with tradeCount = number of agreements in the family; the row then opens the same
// agreement the local's own page defaults to (defaultAgreement over the family's agreements).
// A local whose one agreement here is a class sheet (no single journey rate) gets wage null,
// range = { min, max } across its classes and classCount instead.
export function localSummary(rows, local, familyKey) {
  const inFamily = agreementsForLocal(rows, local).filter(a => familyKeyForTrade(a.trade) === familyKey);
  if (inFamily.length === 0) return { agreement: null, wage: null };
  const mapped = inFamily.find(a => a.agreement === MAIN_AGREEMENT[`${local}|${familyKey}`]);
  if (mapped) return { agreement: mapped, wage: headlineOf(mapped)?.hourly_wage ?? null };
  if (inFamily.length === 1) {
    const classes = classRange(inFamily[0].rows);
    if (classes) return { agreement: inFamily[0], wage: null, range: { min: classes.min, max: classes.max }, classCount: classes.count };
  }
  const wages = inFamily.map(a => headlineOf(a)?.hourly_wage).filter(w => w != null).map(Number);
  const same = wages.length === inFamily.length && wages.every(w => w === wages[0]);
  if (inFamily.length === 1 || same) return { agreement: inFamily[0], wage: headlineOf(inFamily[0])?.hourly_wage ?? null };
  // Several agreements at different wages: use the one whose name starts with "Bricklayers" when
  // there is exactly one (e.g. each BAC Local 79 local); otherwise there is no single wage to show.
  const bricklayers = inFamily.filter(a => BRICKLAYERS_NAME.test(a.agreement));
  if (bricklayers.length === 1) return { agreement: bricklayers[0], wage: headlineOf(bricklayers[0])?.hourly_wage ?? null };
  const range = wages.length >= 2 && Math.min(...wages) < Math.max(...wages)
    ? { min: Math.min(...wages), max: Math.max(...wages) } : null;
  return { agreement: defaultAgreement(inFamily), wage: null, range, tradeCount: inFamily.length };
}

// Union Finder (resource_directory.name, type 'union') -> where its "See published wages" link
// goes. This list is the only source of those links: a union not listed here gets no link, even
// if its name happens to equal a local_name. Add an entry only after checking both names in the
// data; never a fuzzy or guessed match. Each entry is one of:
//   { local }             open that local (default agreement)
//   { local, agreement }  open that local on that agreement
//   { family, locals }    open a trade family's list of locals -- for a union whose wage cards
//                         can't be tied to one local; linked when any of `locals` is published
const DIRECTORY_TO_WAGES = {
  'IBEW Local 134': { local: 'IBEW Local 134' },
  'IBEW Local 701': { local: 'IBEW Local 701' },
  'Glaziers Local 27': { local: 'Glaziers Local 27' },
  'Painters District Council 14': { local: 'Painters District Council 14' },
  'Plumbers Local 130, UA': { local: 'Plumbers Local 130 UA' },
  'Roofers Local 11': { local: 'Roofers & Waterproofers Local 11' },
  // Bricklayer and tile rates are under Local 21; the council's other agreements (BAC ADC 1)
  // stay reachable from the landing cards.
  'Bricklayers & Allied Craftworkers (BAC) District Council 1 of Illinois': {
    local: 'BAC Local 21', agreement: 'Bricklayers and Stone Masons (BAC ADC 1, Local 21 area)',
  },
  'Cement Masons & Plasterers Local 502': { local: 'Cement Masons & Plasterers Local 502' },
  'Pipefitters Local 597, UA': { local: 'Pipefitters Local 597 UA' },
  'IUOE Local 150': { local: 'IUOE Local 150' },
  'Ironworkers Local 1': { local: 'Ironworkers Local 1' },
  'Sheet Metal Workers Local 73': { local: 'Sheet Metal Workers Local 73' },
  'Sheet Metal Workers Local 265': { local: 'Sheet Metal Workers Local 265' },
  'Boilermakers Local 1': { local: 'Boilermakers Local 1' },
  'IUEC Local 2': { local: 'IUEC Local 2' },
  'Sprinkler Fitters Local 281': { local: 'Sprinkler Fitters Local 281' },
  // The council's rates are published under its current name. Both directory names are intentional
  // while the entry is renamed from the old name to the new one, so the card keeps its link before
  // and after the database change; the old key can go once the rename is done.
  'Chicago Regional Council of Carpenters': { local: 'Mid-America Carpenters Regional Council' },
  'Mid-America Carpenters Regional Council (Chicago Regional Council of Carpenters)': { local: 'Mid-America Carpenters Regional Council' },
  // Two wage cards (Westchester funds, Fox Valley funds) and the data doesn't say which applies
  // to which local, so link the Laborer family page, which lists both, rather than pick one.
  "Chicago Laborers' District Council (LIUNA)": {
    family: 'laborer',
    locals: ["Chicago Laborers' District Council (Westchester funds)", "Chicago Laborers' District Council (Fox Valley funds)"],
  },
};

// The /union-wages link for a Union Finder card, or null. Only mapped unions, and only when the
// target has published rows (so a link never lands on an empty page).
export function wageLinkForDirectoryName(name, publishedLocals) {
  const m = Object.prototype.hasOwnProperty.call(DIRECTORY_TO_WAGES, name) ? DIRECTORY_TO_WAGES[name] : null;
  if (!m) return null;
  if (m.family) {
    return m.locals.some(l => publishedLocals.has(l)) ? `/union-wages?${new URLSearchParams({ family: m.family }).toString()}` : null;
  }
  if (!publishedLocals.has(m.local)) return null;
  const q = m.agreement ? { local: m.local, agreement: m.agreement } : { local: m.local };
  return `/union-wages?${new URLSearchParams(q).toString()}`;
}

// Distinct local names with published rates. { names: Set, error }
export async function fetchPublishedLocalNames() {
  const { data, error } = await supabase
    .from('published_wage_rates')
    .select('local_name')
    .eq('is_published', true);
  if (error) return { names: new Set(), error: error.message || 'load failed' };
  return { names: new Set((data || []).map(r => r.local_name)), error: null };
}

// ---- Career Pay Guide "Union scale" block ------------------------------------------------------

// One shared load of the published rows for every pay-card on the page. A failed load is not
// cached, so the next card (or a revisit) tries again; callers render nothing on error.
let wageRatesOnce = null;
export function loadPublishedWageRatesOnce() {
  if (!wageRatesOnce) {
    wageRatesOnce = fetchPublishedWageRates().then(res => {
      if (res.error) wageRatesOnce = null;
      return res;
    });
  }
  return wageRatesOnce;
}

// Career Pay Guide trade (trade_career_data.trade, exact) -> published_wage_rates.trade values
// that are the same work. Conservative: only clear one-to-one matches. A pay-guide trade not
// listed here shows no union block. Deliberately NOT mapped:
//   Industrial Electrician  -- plant/maintenance electrical work; the published IBEW sheets are
//                              construction (inside wireman / telecom) agreements
//   HVAC Technician         -- Local 597's residential / light commercial service sheet is related
//                              work but not the same job, so it isn't used as a union scale here
//   welding, automotive and other non-construction trades -- no union scale in this data
export const PAY_TRADE_UNION_SCALE = {
  'Electrician':          ['Electrician'],
  'Plumber':              ['Plumber'],
  'Mason/Bricklayer':     ['Bricklayer / stone mason'],
  'Painter':              ['Painter'],
  'Glazier':              ['Glazier'],
  'Roofer':               ['Roofer / waterproofer'],
  'Cement Mason':         ['Cement mason'],
  'Construction Laborer': ['Laborer'],
  'Carpenter':            ['Carpenter'],
  // Only the "Pipefitter / steamfitter" sheets (Building Trades, Metal Trades); the headline is
  // Local 597's main one, Building Trades (MAIN_AGREEMENT). Residential service is a different job.
  'Pipefitter/Steamfitter': ['Pipefitter / steamfitter'],
  // IUOE Local 150 has no single journey rate: its Building classes show as a range (classCount).
  'Heavy Equipment Operator': ['Operating engineer'],
  'Ironworker':           ['Ironworker'],
  'Sheet Metal Worker':   ['Sheet metal worker'],
  'Boilermaker':          ['Boilermaker'],
  'Elevator Constructor/Mechanic': ['Elevator constructor'],
};

// Journeyman scale for a pay-guide trade: for each local with a matching agreement, the
// journey-level hourly wage of its main agreement (same rules the Union Wages page uses).
// Returns null when the trade isn't mapped or no local has a journey-level wage.
export function unionScaleForPayTrade(rows, payTrade) {
  const trades = PAY_TRADE_UNION_SCALE[payTrade];
  if (!trades) return null;
  const matching = rows.filter(r => trades.includes(r.trade));
  const familyKey = familyKeyForTrade(trades[0]);
  const locals = [];
  for (const local of [...new Set(matching.map(r => r.local_name))].sort((a, b) => a.localeCompare(b))) {
    const ags = agreementsForLocal(matching, local);
    const main = ags.find(a => a.agreement === MAIN_AGREEMENT[`${local}|${familyKey}`]) || defaultAgreement(ags);
    const head = main ? sectionRows(main.rows).journey[0] : null;
    const classes = main && !head ? classRange(main.rows) : null;
    const isIdol = !!main && IDOL_AGREEMENT.test(main.agreement);
    if (classes) {
      locals.push({ name: local, agreement: main.agreement, wage: classes.min, wageMax: classes.max, classCount: classes.count,
        isEstimate: main.rows.some(r => r.is_estimate), isIdol });
      continue;
    }
    if (!head || head.hourly_wage == null || !Number.isFinite(Number(head.hourly_wage))) continue;
    locals.push({ name: local, agreement: main.agreement, wage: Number(head.hourly_wage), isEstimate: !!head.is_estimate, isIdol });
  }
  if (locals.length === 0) return null;
  // classCount: the one local is a class sheet, so the block counts its classes instead of locals.
  const classCount = locals.length === 1 ? locals[0].classCount ?? null : null;
  return {
    locals, familyKey, classCount,
    min: Math.min(...locals.map(l => l.wage)), max: Math.max(...locals.map(l => l.wageMax ?? l.wage)),
    anyEstimate: locals.some(l => l.isEstimate),
    allIdol: locals.every(l => l.isIdol), anyIdol: locals.some(l => l.isIdol),
  };
}
